import { Suspense, useEffect, useRef, useState } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { Circle, Play, RotateCcw, Zap } from "lucide-react";
import AdReward from "./_adReward";

/**
 * Jixxeo Snake 3D — classic snake, but the play grid is one face of a
 * slowly rotating 3D cube. Real WebGL via three.js. Arrow keys or on-screen
 * d-pad to steer.
 */

const GRID = 14; // 14x14 cells
const CELL = 1;

function idxToPos(x, y) {
  // center grid around origin
  const off = (GRID - 1) / 2;
  return [(x - off) * CELL, 0.5, (y - off) * CELL];
}

function Board({ snakeRef, foodRef, tickRef }) {
  const rootRef = useRef();
  const foodMeshRef = useRef();
  const snakeGroupRef = useRef();

  useFrame((_, dt) => {
    if (rootRef.current) rootRef.current.rotation.y += dt * 0.14;
    if (foodMeshRef.current && foodRef.current) {
      const [fx, , fz] = idxToPos(foodRef.current.x, foodRef.current.y);
      foodMeshRef.current.position.set(fx, 0.55 + Math.sin(performance.now() / 220) * 0.08, fz);
      foodMeshRef.current.rotation.y += dt * 2;
      foodMeshRef.current.rotation.x += dt * 1.3;
    }
    if (snakeGroupRef.current) {
      const snake = snakeRef.current;
      snakeGroupRef.current.children.forEach((m, i) => {
        const s = snake[i];
        if (!s) { m.visible = false; return; }
        m.visible = true;
        const [x, , z] = idxToPos(s.x, s.y);
        m.position.set(x, 0.4, z);
        const t = i === 0 ? 1 : Math.max(0.55, 1 - i * 0.03);
        m.scale.setScalar(t);
      });
    }
  });

  // enough segments for a big snake
  const slots = Array.from({ length: 220 });

  return (
    <group ref={rootRef}>
      {/* cube body */}
      <mesh position={[0, -0.3, 0]}>
        <boxGeometry args={[GRID + 0.6, 0.5, GRID + 0.6]} />
        <meshStandardMaterial color="#0a0a12" metalness={0.4} roughness={0.4} />
      </mesh>
      {/* top play surface */}
      <mesh position={[0, 0, 0]} rotation-x={-Math.PI / 2}>
        <planeGeometry args={[GRID, GRID]} />
        <meshStandardMaterial color="#050510" />
      </mesh>
      {/* grid lines */}
      {Array.from({ length: GRID + 1 }).map((_, i) => {
        const p = i - GRID / 2;
        return (
          <group key={i}>
            <mesh position={[p, 0.01, 0]} rotation-x={-Math.PI / 2}>
              <planeGeometry args={[0.03, GRID]} />
              <meshBasicMaterial color="#00F0FF" transparent opacity={0.18} />
            </mesh>
            <mesh position={[0, 0.01, p]} rotation-x={-Math.PI / 2}>
              <planeGeometry args={[GRID, 0.03]} />
              <meshBasicMaterial color="#00F0FF" transparent opacity={0.18} />
            </mesh>
          </group>
        );
      })}
      {/* food */}
      <mesh ref={foodMeshRef}>
        <icosahedronGeometry args={[0.4, 0]} />
        <meshStandardMaterial color="#FF003C" emissive="#FF003C" emissiveIntensity={1.2} metalness={0.5} roughness={0.2} />
      </mesh>
      {/* snake segments */}
      <group ref={snakeGroupRef}>
        {slots.map((_, i) => (
          <mesh key={i} visible={false}>
            <boxGeometry args={[0.75, 0.75, 0.75]} />
            <meshStandardMaterial
              color={i === 0 ? "#00F0FF" : "#39FF14"}
              emissive={i === 0 ? "#00F0FF" : "#39FF14"}
              emissiveIntensity={0.9}
              metalness={0.4} roughness={0.3}
            />
          </mesh>
        ))}
      </group>
    </group>
  );
}

export default function JixxeoSnake3D() {
  const [running, setRunning] = useState(false);
  const [gameOver, setGameOver] = useState(false);
  const [score, setScore] = useState(0);
  const [best, setBest] = useState(() => Number(localStorage.getItem("jixxeo_snake3d_best") || 0));

  const snakeRef = useRef([{ x: 7, y: 7 }, { x: 6, y: 7 }, { x: 5, y: 7 }]);
  const dirRef = useRef({ x: 1, y: 0 });
  const nextDirRef = useRef({ x: 1, y: 0 });
  const foodRef = useRef({ x: 10, y: 7 });
  const tickRef = useRef(0);
  const aliveRef = useRef(false);

  useEffect(() => {
    if (score > best) { setBest(score); localStorage.setItem("jixxeo_snake3d_best", String(score)); }
  }, [score, best]);

  const placeFood = () => {
    const snake = snakeRef.current;
    let x, y, tries = 0;
    do {
      x = Math.floor(Math.random() * GRID);
      y = Math.floor(Math.random() * GRID);
      tries++;
    } while (snake.some((s) => s.x === x && s.y === y) && tries < 100);
    foodRef.current = { x, y };
  };

  const setDir = (dx, dy) => {
    const cur = dirRef.current;
    if (cur.x === -dx && cur.y === -dy) return; // no 180° turn
    nextDirRef.current = { x: dx, y: dy };
  };

  useEffect(() => {
    const onKey = (e) => {
      if (!running) return;
      const k = e.key;
      if (k === "ArrowUp" || k === "w" || k === "W") setDir(0, -1);
      else if (k === "ArrowDown" || k === "s" || k === "S") setDir(0, 1);
      else if (k === "ArrowLeft" || k === "a" || k === "A") setDir(-1, 0);
      else if (k === "ArrowRight" || k === "d" || k === "D") setDir(1, 0);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [running]);

  // game tick (150 ms)
  useEffect(() => {
    if (!running) return;
    let alive = true;
    let last = performance.now();
    const step = () => {
      if (!alive) return;
      const now = performance.now();
      // speed up slightly with score
      const interval = Math.max(70, 150 - score * 2);
      if (now - last >= interval) {
        last = now;
        dirRef.current = nextDirRef.current;
        const snake = snakeRef.current;
        const head = { x: snake[0].x + dirRef.current.x, y: snake[0].y + dirRef.current.y };
        // wall check
        if (head.x < 0 || head.x >= GRID || head.y < 0 || head.y >= GRID) {
          aliveRef.current = false; setRunning(false); setGameOver(true); return;
        }
        // self check
        if (snake.some((s) => s.x === head.x && s.y === head.y)) {
          aliveRef.current = false; setRunning(false); setGameOver(true); return;
        }
        const newSnake = [head, ...snake];
        // food?
        if (head.x === foodRef.current.x && head.y === foodRef.current.y) {
          setScore((s) => s + 1);
          placeFood();
        } else {
          newSnake.pop();
        }
        snakeRef.current = newSnake;
        tickRef.current++;
      }
      requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
    return () => { alive = false; };
  }, [running, score]);

  const start = () => {
    snakeRef.current = [{ x: 7, y: 7 }, { x: 6, y: 7 }, { x: 5, y: 7 }];
    dirRef.current = { x: 1, y: 0 };
    nextDirRef.current = { x: 1, y: 0 };
    placeFood();
    aliveRef.current = true;
    setScore(0); setGameOver(false); setRunning(true);
  };

  return (
    <div className="min-h-screen bg-[#050510] text-white p-4 md:p-8 relative overflow-hidden flex flex-col">
      <div className="relative max-w-5xl w-full mx-auto flex flex-col">
        <div className="flex items-center gap-3 mb-4">
          <span className="w-10 h-10 rounded-xl bg-[#00F0FF] flex items-center justify-center shadow-[0_0_24px_rgba(0,240,255,0.55)]">
            <Circle className="w-5 h-5 text-black" strokeWidth={3} />
          </span>
          <div>
            <div className="mono text-[9px] uppercase tracking-[0.25em] text-[#00F0FF]">// jixxeo apps · demo pwa · webgl</div>
            <h1 className="font-heading font-black text-2xl md:text-3xl uppercase tracking-tighter leading-none">Jixxeo Snake 3D</h1>
          </div>
          <div className="ml-auto flex items-center gap-2">
            <Chip label="Score" value={score} color="#00F0FF" />
            <Chip label="Best" value={best} color="#39FF14" />
          </div>
        </div>

        <div className="relative rounded-2xl overflow-hidden border border-white/10 bg-black" style={{ aspectRatio: "16 / 9" }}>
          <Canvas
            camera={{ position: [0, 11, 10], fov: 55 }}
            gl={{ antialias: true, powerPreference: "high-performance" }}
          >
            <Suspense fallback={null}>
              <color attach="background" args={["#050510"]} />
              <fog attach="fog" args={["#050510", 12, 40]} />
              <ambientLight intensity={0.5} />
              <pointLight position={[6, 8, 4]} intensity={1.4} color="#00F0FF" distance={30} />
              <pointLight position={[-6, 8, -4]} intensity={1.4} color="#FF003C" distance={30} />
              <Board snakeRef={snakeRef} foodRef={foodRef} tickRef={tickRef} />
            </Suspense>
          </Canvas>

          {/* D-Pad on mobile */}
          <div className="absolute bottom-3 left-3 md:hidden">
            <div className="grid grid-cols-3 gap-1 w-36">
              <div />
              <button onTouchStart={() => setDir(0, -1)} onClick={() => setDir(0, -1)}
                className="w-10 h-10 rounded-lg border border-[#00F0FF]/40 bg-black/60 text-[#00F0FF] font-heading font-black">▲</button>
              <div />
              <button onTouchStart={() => setDir(-1, 0)} onClick={() => setDir(-1, 0)}
                className="w-10 h-10 rounded-lg border border-[#00F0FF]/40 bg-black/60 text-[#00F0FF] font-heading font-black">◀</button>
              <div />
              <button onTouchStart={() => setDir(1, 0)} onClick={() => setDir(1, 0)}
                className="w-10 h-10 rounded-lg border border-[#00F0FF]/40 bg-black/60 text-[#00F0FF] font-heading font-black">▶</button>
              <div />
              <button onTouchStart={() => setDir(0, 1)} onClick={() => setDir(0, 1)}
                className="w-10 h-10 rounded-lg border border-[#00F0FF]/40 bg-black/60 text-[#00F0FF] font-heading font-black">▼</button>
              <div />
            </div>
          </div>

          {(!running || gameOver) && (
            <div className="absolute inset-0 flex items-center justify-center bg-black/70 backdrop-blur-sm">
              <div className="text-center max-w-md px-4">
                <div className="mono text-[10px] uppercase tracking-[0.3em] text-[#00F0FF] mb-2">// slither</div>
                <div className="font-heading font-black text-4xl uppercase tracking-tighter mb-2">
                  {gameOver ? "Game over" : "Eat & grow"}
                </div>
                {gameOver && (
                  <div className="mono text-sm text-white/70 mb-3">Score <span className="text-[#00F0FF] font-bold">{score}</span> · Best <span className="text-[#39FF14] font-bold">{best}</span></div>
                )}
                {gameOver && (
                  <div className="mb-4 flex justify-center"><AdReward game="jixxeo-snake-3d" compact /></div>
                )}
                <div className="mono text-[10px] uppercase tracking-widest text-white/50 mb-6">
                  Arrows / WASD to steer · avoid walls & yourself
                </div>
                <button data-testid="embed-snake3d-start" onClick={start}
                  className="inline-flex items-center gap-2 bg-[#00F0FF] hover:bg-white text-black font-black mono text-xs uppercase tracking-widest px-8 py-3 rounded-full pulse-neon">
                  {gameOver ? <RotateCcw className="w-4 h-4" /> : <Play className="w-4 h-4" fill="#000" />} {gameOver ? "Retry" : "Start"}
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="mono text-[9px] uppercase tracking-widest text-white/40 mt-4 text-center flex items-center gap-2 justify-center">
          <Zap className="w-3 h-3" /> three.js webgl · rotating 3D board · runs entirely in browser
        </div>
      </div>
    </div>
  );
}

function Chip({ label, value, color }) {
  return (
    <div className="hidden md:inline-flex items-center gap-2 mono text-[10px] uppercase tracking-widest px-3 py-1.5 rounded-full border" style={{ borderColor: `${color}55`, background: `${color}10`, color }}>
      <span className="opacity-70">{label}</span>
      <span className="font-bold text-white">{value}</span>
    </div>
  );
}
